import Box from '@mui/material/Box';
import { useTranslation } from 'react-i18next';
import Snackbar from '@mui/material/Snackbar';
import {
  Alert,
  Theme,
  useTheme,
} from '@mui/material';
import React, { useEffect } from 'react';
import { unwrapResult } from '@reduxjs/toolkit';
import { SystemStyleObject } from '@mui/system';
import { useSearchParams } from 'react-router-dom';
import CallToActionButton from '../../../common/CallToActionButton';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import {
  selectCurrentFormValidState,
  selectEnrollMemberStatus,
} from '../../../store/slices/onboardingSlice';
import { getMemberInformation, getOnboardingForm } from '../../../store/thunks/onboardingThunk';
import { TestIds } from '../../../assets/testIds';
import { useCustomEventTracking } from '../../../common/useCustomEventTracking';
import { OnboardingStepper } from '../../../common/OnboardingStepper';
import { OnboardBusinessInformationWrapperV2 } from '../BusinessInformation/OnboardBusinessInformationWrapperV2';
import { ONBOARDING_FORMS } from '../constants';
import { getCompanyPartnerRelations } from '../../../store/thunks/dashboardThunk';

interface OnboardFormV2Props {
  onboardFormId: string;
  optionalContainerStyle?: (theme: Theme) => SystemStyleObject<Theme>;
  optionalBtnStyles?: {
    [k: string]: unknown;
  };
  optionalBackgroundColor?: string;
  setIsOnboarding?: (value: boolean) => void;
  OptionalHeader?: JSX.Element;
  noFooterText?: boolean;
  customBusinessInfoBtnText?:string
}

export function OnboardFormV2(props : OnboardFormV2Props) {
  const { t } = useTranslation();
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const isFormValid = useAppSelector(selectCurrentFormValidState);
  const enrollMemberStatus = useAppSelector(selectEnrollMemberStatus);
  const [openSnackbar, setOpenSnackbar] = React.useState(false);
  const [searchParams] = useSearchParams();
  const memberId = searchParams.get('memberId') || undefined;
  const { trackRequest } = useCustomEventTracking();
  const activeStep = 0;

  useEffect(() => {
    const getOnboardingFormResponse = dispatch(getOnboardingForm(
      {
        formId: props.onboardFormId,
        trackRequest,
      },
    ))
      .then(unwrapResult);
    const getMemberInformationResponse = dispatch(getMemberInformation(
      {
        id: memberId,
        trackRequest,
      },
    ))
      .then(unwrapResult)
      .then((memberInfoResponse) => {
        dispatch(getCompanyPartnerRelations(memberInfoResponse?.company?.id as string));
      });
    Promise.all(
      [getOnboardingFormResponse, getMemberInformationResponse],
    )
      .then(() => {
        setOpenSnackbar(false);
      })
      .catch(() => {
        setOpenSnackbar(true);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleClose = () => {
    setOpenSnackbar(false);
  };

  return (
    <>
      <Snackbar open={openSnackbar} autoHideDuration={4000} onClose={handleClose}>
        <Alert severity="error" sx={{ width: '100%' }}>
          {t('error.generalMessage')}
        </Alert>
      </Snackbar>
      <Box sx={{
        borderRadius: theme.spacing(1.5),
        paddingTop: theme.spacing(3.125),
        paddingBottom: theme.spacing(3.125),
        width: '100%',
      }}>
        <Box sx={{ maxWidth: '430px', ml: 'auto', mr: 'auto' }}>
          <OnboardingStepper
            stepLabels={['Step 1', 'Step 2']}
            activeStep={activeStep}
          />
        </Box>
        <OnboardBusinessInformationWrapperV2 />
        <CallToActionButton
          form={ONBOARDING_FORMS.businessInformation.id}
          text={t('button.saveAndProceed') as string}
          data-testid={TestIds.onboarding.nextBtn}
          disable={ !isFormValid || enrollMemberStatus === 'pending'}
          type='submit'
          sx={{
            position: 'static',
            width: '100%',
            [theme.breakpoints.up('sm')]: {
              width: '100%',
              marginLeft: '0px',
            },
            ...(props.optionalBtnStyles && props.optionalBtnStyles),
          }}
        />
      </Box>
    </>
  );
}
